import {useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../../page/main/reviews/reviews-state/reviews-state-type';
import {useReviewsStatus, useReviewStatus} from '../../page/main/reviews/reviews-state/reviews-status-hook';
import {useUser} from '../../provider/user/user-hook';
import {deserializeV2, postAndDeserialize} from '../../util/api-adapter';
import {useCursorPagination} from '../../util/cursor-pagination/cursor-pagination-hook';
import {CursorPaginationDataType, CursorPaginationType} from '../../util/cursor-pagination/cursor-pagination-type';
import {createRaceFetchX, FetchMethodEnum, fetchX} from '../../util/fetch';
import {objectToUrlParameters} from '../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';

import {getReviewsUrlForRatingBucket, reviewsStateToUrlObject} from './reviews-helper';
import {fetchReviewsWithRatingSort, RatingSortPaginationBrokenError} from './reviews-rating-sort-helper';
import {
    isRatingSortPageStartSnapshotValid,
    loadRatingSortPagination,
    resetRatingSortPagination,
} from './reviews-rating-sort-pagination-helper';
import {isRatingSort} from './reviews-sort-helper';
import {
    BulkReviewNotesRequestType,
    bulkReviewNotesResultSchema,
    BulkReviewNotesResultType,
    BulkReviewTagsRequestType,
    bulkReviewTagsResultSchema,
    BulkReviewTagsResultType,
    fetchReviewsResultSchema,
    FetchReviewsResultType,
    reviewDataSchema,
    ReviewDataType,
    ReviewSentimentType,
    ReviewsReplyTagsType,
    ReviewTagsPutType,
} from './reviews-type';

const reviewsRaceFetchX = createRaceFetchX();

type ReviewsFetchPaginationType = CursorPaginationDataType & {
    page: number;
    storageKey: string;
};

function getReviewsUrl(reviewOption: ReviewsStateType, paginationData: CursorPaginationDataType) {
    const {pageSize, ...restPaginationData} = paginationData;

    const urlParameters = objectToUrlParameters({
        ...(!reviewOption.company_ids ? {filter_key: reviewOption.filterId} : null),
        ...reviewsStateToUrlObject(reviewOption),
        per_page: pageSize,
        ...restPaginationData,
    });

    return `${rootApiUrl}/v4/reviews/?${urlParameters}`;
}

async function fetchRatingBucket(
    reviewOption: ReviewsStateType,
    rating: number,
    perPage: number,
    cursor: string
): Promise<FetchReviewsResultType> {
    const url = getReviewsUrlForRatingBucket(reviewOption, {rating, pageSize: perPage, cursor});
    const response = await reviewsRaceFetchX(url);

    return deserializeV2<FetchReviewsResultType>(url, fetchReviewsResultSchema, response);
}

async function fetchReviews(
    reviewOption: ReviewsStateType,
    paginationData: ReviewsFetchPaginationType
): Promise<FetchReviewsResultType> {
    const sort = reviewOption[ReviewsStateUrlQueryNameEnum.sorting];
    const {pageSize, page, storageKey} = paginationData;

    if (isRatingSort(sort)) {
        const {result} = await fetchReviewsWithRatingSort({
            reviewsState: reviewOption,
            pageSize,
            page,
            storageKey,
            fetchRatingBucket: (rating, perPage, cursor) => fetchRatingBucket(reviewOption, rating, perPage, cursor),
        });

        return result;
    }

    const url = getReviewsUrl(reviewOption, paginationData);
    const response = await reviewsRaceFetchX(url);

    return deserializeV2<FetchReviewsResultType>(url, fetchReviewsResultSchema, response);
}

function buildReviewsQueryKey(
    reviewsState: ReviewsStateType,
    paginationData: ReviewsFetchPaginationType,
    isShowAutotags: boolean | undefined
): string {
    const sort = reviewsState[ReviewsStateUrlQueryNameEnum.sorting];

    if (isRatingSort(sort)) {
        return `rating-sort:${paginationData.storageKey}:${paginationData.page}:${paginationData.pageSize}:${sort}:${isShowAutotags}`;
    }

    return `${getReviewsUrl(reviewsState, paginationData)}${isShowAutotags}`;
}

export function useReviews(
    reviewsState: ReviewsStateType
): UseQueryResult<FetchReviewsResultType> & CursorPaginationType & {queryKey: string; reviewIds: Array<number>} {
    const queryClient = useQueryClient();
    const paginationDependencies = useMemo(() => {
        return {mainFilterKey: reviewsState.filterId, reviewsState};
    }, [reviewsState]);

    const cursorPagination = useCursorPagination({
        dependencies: paginationDependencies,
    });
    const {pageSize, cursor, onDataLoaded, page, storageKey, changePagePagination} = cursorPagination;
    const {user} = useUser();
    const paginationData: ReviewsFetchPaginationType = {cursor, pageSize, page, storageKey};
    const sort = reviewsState[ReviewsStateUrlQueryNameEnum.sorting];
    const isRatingSortMode = isRatingSort(sort);
    const queryKey = buildReviewsQueryKey(reviewsState, paginationData, user?.profile?.isShowAutotags);
    const previousDependenciesRef = useRef(paginationDependencies);
    const previousPageSizeRef = useRef(pageSize);

    const resetRatingSortState = useCallback(() => {
        resetRatingSortPagination(storageKey, queryClient);
    }, [queryClient, storageKey]);

    useEffect(() => {
        if (previousDependenciesRef.current !== paginationDependencies) {
            resetRatingSortState();
        }

        previousDependenciesRef.current = paginationDependencies;
    }, [paginationDependencies, resetRatingSortState]);

    useEffect(() => {
        if (previousPageSizeRef.current !== pageSize) {
            resetRatingSortState();
        }

        previousPageSizeRef.current = pageSize;
    }, [pageSize, resetRatingSortState]);

    useEffect(() => {
        if (!isRatingSortMode || page <= 1) {
            return;
        }

        const store = loadRatingSortPagination(storageKey);

        if (!isRatingSortPageStartSnapshotValid(store, page)) {
            resetRatingSortState();
            changePagePagination(1);
        }
    }, [changePagePagination, isRatingSortMode, page, resetRatingSortState, storageKey]);

    const query = useQuery([queryKey], () => fetchReviews(reviewsState, paginationData), {
        onSuccess: (data) => onDataLoaded(data),
        onError: (error) => {
            if (error instanceof RatingSortPaginationBrokenError) {
                resetRatingSortState();
                changePagePagination(1);
            }
        },
        refetchOnMount: false,
        keepPreviousData: !isRatingSortMode,
        ...(isRatingSortMode ? {staleTime: 0} : null),
        refetchOnWindowFocus: false,
    });

    const reviewsIds = query.data?.results.map((review) => review.id) || [];

    useReviewsStatus(reviewsIds, queryKey);

    return {
        ...query,
        pagination: cursorPagination,
        queryKey,
        reviewIds: reviewsIds,
    };
}

export function getReviewItemUrl(id: number): string {
    return `${rootApiUrl}/v4/reviews/${id}/`;
}

export async function fetchReviewItem(id: number): Promise<ReviewDataType> {
    const url = getReviewItemUrl(id);
    const response = await fetchX<ReviewDataType>(url);

    return deserializeV2<ReviewDataType>(url, reviewDataSchema, response);
}

export function useReviewItem(id: number): UseQueryResult<ReviewDataType> & {queryKey: string} {
    const query = useQuery([getReviewItemUrl(id)], () => fetchReviewItem(id), {
        refetchOnWindowFocus: false,
    });

    useReviewStatus(getReviewItemUrl(id), query.data?.id);

    return {...query, queryKey: getReviewItemUrl(id)};
}

export function fetchReviewsReplyTagsSend(reviewId: number, data: ReviewsReplyTagsType): Promise<ReviewTagsPutType> {
    return fetchX<ReviewTagsPutType>(`${rootApiUrl}/v4/reviews/${reviewId}/tags/`, {
        method: FetchMethodEnum.put,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(data),
    });
}

export function postBulkReviewTags(payload: BulkReviewTagsRequestType): Promise<BulkReviewTagsResultType> {
    return postAndDeserialize<BulkReviewTagsResultType>('/v4/reviews/tags/', bulkReviewTagsResultSchema, {
        reviewIds: payload.reviewIds,
        tagIds: payload.tagIds,
    });
}

export function postBulkReviewNotes(payload: BulkReviewNotesRequestType): Promise<BulkReviewNotesResultType> {
    return postAndDeserialize<BulkReviewNotesResultType>('/v4/reviews/notes/bulk/', bulkReviewNotesResultSchema, {
        reviewIds: payload.reviewIds,
        text: payload.text,
    });
}

export function fetchReviewSentimentUpdate(reviewId: number, data: {sentiment: ReviewSentimentType}): Promise<void> {
    return fetchX<void>(`${rootApiUrl}/v4/reviews/${reviewId}/sentiment/`, {
        method: FetchMethodEnum.patch,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(data),
    });
}
